/* eslint-disable react/destructuring-assignment */
import React from 'react'
import dayjs from 'dayjs'
import { Observable, Subject } from 'rx'
import { navigateToDepositLimitsEdit } from '@classic/AppUtils/Framework/Intent/navigation'
import { queryClient } from '@core/Data/ReactQuery/queryClient'
import { queryKeys } from '@mobi/account/Utils'
import type { BetAccount, BamAccountHolder } from '@mobi/api-types'
import { betAccountQueryOptions } from '@mobi/account/Hooks/useBetAccount'
import {
  updateDepositLimitAnniversary,
  type UpdateDepositLimitAnniversaryRequest,
} from '@mobi/account/Areas/SignUp/Utils/SignUpApi'
import { AnniversaryPopupContent } from '../AnniversaryPopupContent/AnniversaryPopupContent'
import {
  ErrorPopupNoticeBox,
  SuccessPopupNoticeBox,
} from '../DepositLimitsComponents/DepositLimitsComponents'
import { PopupContainerStyled, PopupTitleStyled } from '@core/Components/Popup/Popup.styles'
import { AnniversaryPopup } from './DepositLimitsAnniversaryPopup.styles'
import { TABLocation } from '@core/Settings/constants'

import { state$ as UserAccountState$ } from '@core/State/UserAccount/userAccountDriver'
import { state$ as RouteState$ } from '@core/State/Navigation'

const DAYS_BEFORE_ANNIVERSARY_REMINDER = 14

export interface DepositLimitsAnniversaryPopupProps {
  isDesktop?: boolean
}

export enum DisplayState {
  HideAll,
  Prompt,
  Success,
  Error,
}

export interface DepositLimitsAnniversaryPopupState {
  displayState: DisplayState
  betAccountNumber: number | null
  accountHolder: BamAccountHolder | null
}

export const DepositLimitsAnniversaryPopup = ({
  isDesktop,
}: DepositLimitsAnniversaryPopupProps) => {
  return <DepositLimitsAnniversaryPopupComponent isDesktop={isDesktop} />
}

export class DepositLimitsAnniversaryPopupComponent extends React.Component<
  DepositLimitsAnniversaryPopupProps,
  DepositLimitsAnniversaryPopupState
> {
  private subscription?: Rx.Disposable
  private refetchTrigger$ = new Subject<void>()

  constructor(props: DepositLimitsAnniversaryPopupProps) {
    super(props)
    this.state = {
      displayState: DisplayState.HideAll,
      betAccountNumber: null,
      accountHolder: null,
    }
  }

  public componentDidMount() {
    const loginStateChange$ = UserAccountState$.distinctUntilChanged(state => state.isLoggedIn)

    const fetchTrigger$ = loginStateChange$.merge(
      this.refetchTrigger$.withLatestFrom(loginStateChange$, (_, state) => state)
    )

    const betAccountData$ = fetchTrigger$.flatMapLatest(userState =>
      !userState.isLoggedIn
        ? Observable.just<BetAccount | null>(null)
        : Observable.fromPromise(queryClient.fetchQuery(betAccountQueryOptions).catch(() => null))
    )

    const allState = Observable.combineLatest(
      betAccountData$,
      UserAccountState$,
      RouteState$,
      (betAccount, userAccountState, routeState) => ({
        betAccount,
        userAccountState,
        routeState,
      })
    ).distinctUntilChanged(
      true,
      (x, y) =>
        Object.is(x.betAccount, y.betAccount) &&
        Object.is(x.routeState, y.routeState) &&
        Object.is(x.userAccountState, y.userAccountState)
    )

    const setHideAll = () =>
      this.setState({
        displayState: DisplayState.HideAll,
        betAccountNumber: null,
        accountHolder: null,
      })

    this.subscription = allState.subscribe(s => {
      if (!s.userAccountState.isLoggedIn) {
        queryClient.removeQueries({ queryKey: [queryKeys.betAccount] })
        setHideAll()
      } else {
        const betAccount = s.betAccount as BetAccount | null

        // conscious decision to not display error or loading as we render on the home page.
        if (!betAccount) {
          setHideAll()
          return
        }

        if (betAccount.AccountHolderList.length === 0) {
          setHideAll()
          return
        }

        this.setState({ betAccountNumber: betAccount.BetAccountNumber })

        const getActiveAccountHolder = (accountHolders: BamAccountHolder[]) => {
          const isInDateRange = (anniversary: Date | null) =>
            !!(
              anniversary &&
              dayjs(anniversary).diff(dayjs(), 'days', true) <= DAYS_BEFORE_ANNIVERSARY_REMINDER
            )

          const primaryAccountHolder =
            accountHolders.find(ah => ah.IsPrimaryAccountHolder) || accountHolders[0]
          const primaryAccountHolderIsInDateRange = isInDateRange(
            primaryAccountHolder.PreCommitmentAnniversaryDate
          )
          const secondaryAccountHolder =
            accountHolders.find(ah => !ah.IsPrimaryAccountHolder) || accountHolders.length === 2
              ? accountHolders[1]
              : null
          const secondaryAccountHolderIsInDateRange =
            secondaryAccountHolder &&
            isInDateRange(secondaryAccountHolder.PreCommitmentAnniversaryDate)

          if (primaryAccountHolderIsInDateRange && !secondaryAccountHolderIsInDateRange) {
            return primaryAccountHolder
          }

          if (!primaryAccountHolderIsInDateRange && secondaryAccountHolderIsInDateRange) {
            return secondaryAccountHolder
          }

          if (primaryAccountHolderIsInDateRange && secondaryAccountHolderIsInDateRange) {
            return primaryAccountHolder.PreCommitmentAnniversaryDate &&
              secondaryAccountHolder &&
              secondaryAccountHolder.PreCommitmentAnniversaryDate &&
              primaryAccountHolder.PreCommitmentAnniversaryDate <=
                secondaryAccountHolder.PreCommitmentAnniversaryDate
              ? primaryAccountHolder
              : secondaryAccountHolder
          }

          return null
        }

        const accountHolder = getActiveAccountHolder(betAccount.AccountHolderList)
        this.setState({ accountHolder })

        const isLoggedIn = s.userAccountState.isLoggedIn
        const isOnHomePage = s.routeState.get('currentUrl') === '/'
        const displayState: DisplayState =
          isLoggedIn && isOnHomePage && accountHolder !== null
            ? DisplayState.Prompt
            : DisplayState.HideAll

        this.setState({ displayState })
      }
    })
  }

  public componentWillUnmount() {
    this.subscription?.dispose()
  }

  public render() {
    if (this.state.displayState === DisplayState.HideAll) {
      return null
    }

    if (this.state.displayState === DisplayState.Error) {
      return <ErrorPopupNoticeBox onClose={() => this.closeAll(false)} />
    }

    if (this.state.displayState === DisplayState.Success) {
      return <SuccessPopupNoticeBox onClose={() => this.closeAll(true)} />
    }

    if (this.state.displayState !== DisplayState.Prompt || !this.state.accountHolder) {
      return null
    }

    return (
      <PopupContainerStyled>
        <AnniversaryPopup isDesktop={this.props.isDesktop}>
          <PopupTitleStyled isDesktop={this.props.isDesktop} data-tid-anniversary-deposit-limit=''>
            Deposit Limit
          </PopupTitleStyled>
          <AnniversaryPopupContent
            isDesktop={this.props.isDesktop}
            accountHolder={this.state.accountHolder}
            onKeepLimit={this.keepLimit}
            onChangeLimit={this.changeLimit}
          />
        </AnniversaryPopup>
      </PopupContainerStyled>
    )
  }

  private closeAll = (invalidate: boolean) => {
    if (invalidate) {
      queryClient.removeQueries({ queryKey: [queryKeys.betAccount] })
      this.refetchTrigger$.onNext()
    }

    this.setState({ displayState: DisplayState.HideAll })
  }

  private respondToDepositLimitAnniversary = (keep: boolean) => {
    // deal with being invoked before all relevant state is available.
    if (!this.state.betAccountNumber) {
      return Promise.resolve({})
    }

    const request: UpdateDepositLimitAnniversaryRequest = {
      AccountHolderId: (this.state.accountHolder as BamAccountHolder).AccountHolderId,
      BetAccountNumber: this.state.betAccountNumber,
      Keep: keep,
      Location: !this.props.isDesktop ? TABLocation.Mobi : TABLocation.Desktop,
    }

    return updateDepositLimitAnniversary(request)
  }

  private keepLimit = () => {
    this.respondToDepositLimitAnniversary(true)
      .then(() => {
        this.setState({ displayState: DisplayState.Success, betAccountNumber: null })
      })
      .catch(() => {
        this.setState({ displayState: DisplayState.Error })
      })
  }

  private changeLimit = () => {
    this.respondToDepositLimitAnniversary(false)
      .then(() => {
        const accountHolderNumber = this.state.accountHolder
          ? (this.state.accountHolder as BamAccountHolder).AccountHolderId
          : undefined
        queryClient.removeQueries({ queryKey: [queryKeys.betAccount] })
        this.refetchTrigger$.onNext()
        navigateToDepositLimitsEdit({ isDesktop: this.props.isDesktop, accountHolderNumber })
      })
      .catch(() => {
        this.setState({ displayState: DisplayState.Error })
      })
  }
}
